import React, { useState } from 'react';
import { X, Sparkles, AlertTriangle } from 'lucide-react';
import { useLifely } from '../../context/LifelyContext';
import { usePlan } from '../../context/PlanContext';

const DISMISS_KEY = 'lifely_trial_banner_dismissed';

function daysUntil(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms)) return null;
  return Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)));
}

/**
 * Dismissible banner shown to trial users. Subtle when >3 days remain,
 * amber warning when <=3 days. Dismissal is remembered per phase in localStorage,
 * so the warning still resurfaces even if the info banner was dismissed.
 */
const TrialBanner = () => {
  const { user } = useLifely();
  const { openPlansModal } = usePlan();

  const plan = user?.plan;
  const days = daysUntil(user?.trialExpires);
  const phase = days !== null && days <= 3 ? 'warning' : 'info';

  const [dismissedPhase, setDismissedPhase] = useState(() => {
    try {
      return localStorage.getItem(DISMISS_KEY);
    } catch {
      return null;
    }
  });

  if (plan !== 'trial') return null;
  if (dismissedPhase === phase) return null;

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, phase);
    } catch {
      /* ignore */
    }
    setDismissedPhase(phase);
  };

  const isWarning = phase === 'warning';

  let message;
  if (days === null) {
    message = "You're on a free trial";
  } else if (isWarning) {
    message = `Your trial ends in ${days} ${days === 1 ? 'day' : 'days'} · Upgrade now to keep your data`;
  } else {
    message = `You're on a free trial · ${days} ${days === 1 ? 'day' : 'days'} remaining`;
  }

  const containerCls = isWarning
    ? 'bg-amber-50 dark:bg-amber-950/30 border-amber-300 dark:border-amber-800 text-amber-900 dark:text-amber-200'
    : 'bg-muted/60 border-border text-foreground';

  return (
    <div
      className={`mb-6 flex items-center gap-3 rounded-lg border px-4 py-2.5 text-sm ${containerCls}`}
      data-testid="trial-banner"
    >
      {isWarning ? (
        <AlertTriangle className="w-4 h-4 flex-shrink-0 text-amber-600 dark:text-amber-400" />
      ) : (
        <Sparkles className="w-4 h-4 flex-shrink-0 text-primary" />
      )}
      <span className="flex-1">{message}</span>
      <button
        type="button"
        onClick={() => openPlansModal(isWarning ? 'trial_expired' : 'upgrade_prompt')}
        className="font-medium underline underline-offset-2 hover:opacity-80"
        data-testid="trial-banner-upgrade"
      >
        Upgrade
      </button>
      <button
        type="button"
        onClick={handleDismiss}
        className="opacity-60 hover:opacity-100"
        aria-label="Dismiss"
        data-testid="trial-banner-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default TrialBanner;
